import { useState } from 'react';
import { X, Check, Calendar, Clock, MapPin, User, CreditCard, QrCode, Edit2 } from 'lucide-react';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from './ui/drawer';
import { toast } from 'sonner';

export const BookingDrawer = ({ isOpen, onClose, bookingData }) => {
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  if (!bookingData) return null;

  const { service, addOns = [], specialist, branch, time } = bookingData;

  const totalPrice = service.price + addOns.reduce((sum, a) => sum + a.price, 0);
  const totalDuration = service.duration + addOns.reduce((sum, a) => sum + a.duration, 0);
  const depositAmount = Math.round(totalPrice * 0.2);

  const handleConfirmBooking = () => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error('Por favor completa tus datos');
      return;
    }
    
    setCheckoutStep(2);
    setIsConfirmed(true);
    toast.success('¡Cita confirmada! Te enviamos los detalles por WhatsApp');
  };

  const handleClose = () => {
    setCheckoutStep(0);
    setIsConfirmed(false);
    setCustomerInfo({ name: '', phone: '', email: '' });
    onClose();
  };

  const generateConfirmationCode = () => {
    return `CONF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  };

  const confirmationCode = generateConfirmationCode();

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="max-h-[90vh]" data-testid="booking-drawer">
        <div className="drawer-content overflow-y-auto">
          <DrawerHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <DrawerTitle className="font-heading text-2xl">
                {isConfirmed ? 'Cita Confirmada' : 'Resumen de Cita'}
              </DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon">
                  <X className="w-5 h-5" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="p-4 sm:p-6 space-y-6">
            {/* Booking Summary */}
            <div className="space-y-4 p-4 bg-soft-gray/30 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-copper" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{service.name}</p>
                  {addOns.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      + {addOns.map(a => a.name).join(', ')}
                    </p>
                  )}
                </div>
                {!isConfirmed && (
                  <button className="text-copper hover:text-copper-dark">
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-copper" />
                </div>
                <div>
                  <p className="font-medium">{specialist.name}</p>
                  <p className="text-sm text-muted-foreground">{specialist.role}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-copper" />
                </div>
                <div>
                  <p className="font-medium">{branch.name}</p>
                  <p className="text-sm text-muted-foreground">{branch.address}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-copper" />
                </div>
                <div>
                  <p className="font-medium">{time.time}</p>
                  <p className="text-sm text-muted-foreground">Duración: {totalDuration} min</p>
                </div>
              </div>

              <div className="pt-4 border-t border-border/50 flex justify-between items-center">
                <span className="font-medium">Total</span>
                <span className="text-xl font-heading text-copper">${totalPrice} MXN</span>
              </div>
            </div>

            {/* Checkout Steps */}
            {!isConfirmed && checkoutStep === 0 && (
              <div className="space-y-4 animate-fade-in" data-testid="checkout-step-1">
                <h3 className="font-heading text-lg">Tus datos</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Tu nombre *"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:border-copper"
                    data-testid="input-name"
                  />
                  <input
                    type="tel"
                    placeholder="WhatsApp * (para confirmación)"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:border-copper"
                    data-testid="input-phone"
                  />
                  <input
                    type="email"
                    placeholder="Email (opcional)"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:border-copper"
                    data-testid="input-email"
                  />
                </div>
                <Button
                  onClick={() => setCheckoutStep(1)}
                  disabled={!customerInfo.name || !customerInfo.phone}
                  className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-full"
                  data-testid="continue-to-payment"
                >
                  Continuar
                </Button>
              </div>
            )}

            {!isConfirmed && checkoutStep === 1 && (
              <div className="space-y-4 animate-fade-in" data-testid="checkout-step-2">
                <h3 className="font-heading text-lg">Método de pago</h3>
                
                <div className="space-y-3">
                  {[
                    { id: 'efectivo', label: 'Efectivo en sucursal', icon: '💵' },
                    { id: 'tarjeta', label: 'Tarjeta (pago en sucursal)', icon: '💳' }
                  ].map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`w-full p-4 rounded-xl border text-left flex items-center gap-3 transition-colors ${
                        paymentMethod === method.id
                          ? 'border-copper bg-copper/5'
                          : 'border-border hover:border-copper/50'
                      }`}
                    >
                      <span className="text-2xl">{method.icon}</span>
                      <span className="font-medium">{method.label}</span>
                      {paymentMethod === method.id && (
                        <Check className="w-5 h-5 text-copper ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Deposit option */}
                <div className="p-4 bg-copper/5 rounded-xl border border-copper/20">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={depositEnabled}
                      onChange={(e) => setDepositEnabled(e.target.checked)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">Pagar depósito (${depositAmount})</p>
                      <p className="text-sm text-muted-foreground">
                        Asegura tu cita y evita cancelaciones. El resto se paga en sucursal.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCheckoutStep(0)}
                    className="flex-1 py-3 rounded-full"
                  >
                    Atrás
                  </Button>
                  <Button
                    onClick={handleConfirmBooking}
                    className="flex-1 bg-copper hover:bg-copper-dark text-cream py-3 rounded-full"
                    data-testid="confirm-final-btn"
                  >
                    Confirmar cita
                  </Button>
                </div>
              </div>
            )}

            {/* Confirmation */}
            {isConfirmed && (
              <div className="text-center space-y-6 animate-fade-in" data-testid="confirmation-step">
                <div className="w-20 h-20 mx-auto bg-muted-green/20 rounded-full flex items-center justify-center">
                  <Check className="w-10 h-10 text-muted-green" />
                </div>

                <div>
                  <p className="text-muted-foreground mb-2">Código de confirmación</p>
                  <p className="font-heading text-2xl tracking-wider">{confirmationCode}</p>
                </div>

                {/* Mock QR */}
                <div className="w-40 h-40 mx-auto bg-white border-2 border-border rounded-xl flex items-center justify-center">
                  <QrCode className="w-24 h-24 text-graphite" />
                </div>

                <p className="text-sm text-muted-foreground">
                  Muestra este código al llegar a tu cita
                </p>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => {
                      toast.success('Cita agregada al calendario');
                    }}
                    className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-full"
                    data-testid="add-to-calendar"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Agregar al calendario
                  </Button>
                  <a
                    href={`https://wa.me/528112345678?text=Mi%20cita%20${confirmationCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full whatsapp-btn py-3 rounded-full text-center font-medium"
                    data-testid="whatsapp-confirmation"
                  >
                    Enviar a WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default BookingDrawer;
